import { createContext, ReactNode, useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  AlertColor,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface AlertData {
  message: string;
  description?: string;
  severity: AlertColor;
}

interface AlertInput {
  message: string;
  description?: string;
}

interface ConfirmData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  severity?: "warning" | "error";
}

interface AlertContextType {
  alert: {
    success: (data: AlertInput) => void;
    error: (data: AlertInput) => void;
    info: (data: AlertInput) => void;
    warning: (data: AlertInput) => void;
  };
  toast: {
    success: (data: AlertInput) => void;
    error: (data: AlertInput) => void;
    info: (data: AlertInput) => void;
    warning: (data: AlertInput) => void;
  };
  confirm: (data: ConfirmData) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

const iconMap = {
  success: CheckCircleOutline,
  error: ErrorOutline,
  info: InfoOutlined,
  warning: WarningAmberOutlined,
};

const colorMap = {
  success: "#4caf50",
  error: "#f44336",
  info: "#2196f3",
  warning: "#ff9800",
};

export function AlertProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [alertData, setAlertData] = useState<AlertData | null>(null);
  const [open, setOpen] = useState(false);
  const [confirmData, setConfirmData] = useState<ConfirmData | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [toastData, setToastData] = useState<AlertData | null>(null);
  const [toastOpen, setToastOpen] = useState(false);

  const showAlert = (data: AlertInput, severity: AlertColor) => {
    setAlertData({
      message: data.message,
      description: data.description,
      severity,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const success = (data: AlertInput) => showAlert(data, "success");
  const error = (data: AlertInput) => showAlert(data, "error");
  const info = (data: AlertInput) => showAlert(data, "info");
  const warning = (data: AlertInput) => showAlert(data, "warning");

  const showToast = (data: AlertInput, severity: AlertColor) => {
    setToastData({
      message: data.message,
      description: data.description,
      severity,
    });
    setToastOpen(true);
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  const toastSuccess = (data: AlertInput) => showToast(data, "success");
  const toastError = (data: AlertInput) => showToast(data, "error");
  const toastInfo = (data: AlertInput) => showToast(data, "info");
  const toastWarning = (data: AlertInput) => showToast(data, "warning");

  const confirm = (data: ConfirmData) => {
    setConfirmData(data);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (isConfirming) return;
    setConfirmOpen(false);
    confirmData?.onCancel?.();
  };

  const handleConfirm = async () => {
    if (!confirmData || isConfirming) return;
    setIsConfirming(true);
    try {
      await confirmData.onConfirm();
      setConfirmOpen(false);
    } catch (err) {
      console.error("Confirm action failed:", err);
    } finally {
      setIsConfirming(false);
    }
  };

  const Icon = alertData ? iconMap[alertData.severity] : null;
  const iconColor = alertData ? colorMap[alertData.severity] : undefined;

  return (
    <AlertContext.Provider
      value={{
        alert: { success, error, info, warning },
        toast: {
          success: toastSuccess,
          error: toastError,
          info: toastInfo,
          warning: toastWarning,
        },
        confirm,
      }}
    >
      {children}
      {/* Alert Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            pb: 1,
          }}
        >
          {Icon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: iconColor,
              }}
            >
              <Icon fontSize="large" />
            </Box>
          )}
          {alertData?.message}
        </DialogTitle>
        {alertData?.description && (
          <DialogContent>
            <DialogContentText>{alertData.description}</DialogContentText>
          </DialogContent>
        )}
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={handleConfirmClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            pb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              color:
                confirmData?.severity === "error"
                  ? colorMap.error
                  : colorMap.warning,
            }}
          >
            <WarningAmberOutlined fontSize="large" />
          </Box>
          {confirmData?.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: "center" }}>
            {confirmData?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "center", gap: 1 }}>
          <Button
            onClick={handleConfirmClose}
            variant="outlined"
            disabled={isConfirming}
          >
            {confirmData?.cancelText || t("cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={confirmData?.severity === "error" ? "error" : "warning"}
            disabled={isConfirming}
            autoFocus
          >
            {confirmData?.confirmText || t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Snackbar */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleToastClose}
          severity={toastData?.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastData?.message}
          {toastData?.description ? (
            <>
              <br />
              <span style={{ marginTop: "4px", opacity: 0.7 }}>
                {toastData.description}
              </span>
            </>
          ) : null}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
}

export function useAlert() {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
}
